import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  AbsenceSchema,
  AbsenceCreateInputSchema,
  AbsenceUpdateInputSchema,
  type Absence,
  type AbsenceCreateInput,
  type AbsenceUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `absence` (indisponibilité déclarée, org-wide).
 *
 * Même patron que `asset` : l'entrée est VALIDÉE par Zod
 * (`AbsenceCreateInputSchema`) AVANT toute écriture Firestore. Les liens vers
 * d'autres familles (worker, shift, availability, booking, credit) sont de
 * simples ids ; `workerId` (la personne indisponible) est requis. Le motif
 * sensible (kind) et le justificatif ne transitent pas par ce repository :
 * ils vivront dans un sous-document privé (cf. WIP), hors v0.
 */

export type { AbsenceCreateInput };

/** Nom de la collection Firestore de la famille absence. */
export const ABSENCES_COLLECTION = 'absences';

/**
 * Repository typé pour la famille `absence`.
 * Toute lecture est validée par `AbsenceSchema`, toute écriture par
 * `AbsenceCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface AbsenceRepository {
  create(input: AbsenceCreateInput): Promise<Absence>;
  list(options?: ListOptions): Promise<Absence[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Absence | null>;
  update(id: string, patch: AbsenceUpdateInput): Promise<Absence>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Absence>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Absence>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Absence>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const absenceConverter = createConverter<Absence>((raw: DocumentData) => AbsenceSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createAbsenceRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): AbsenceRepository {
  const col = collection(db, ABSENCES_COLLECTION).withConverter(absenceConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Absence>(db, col, ABSENCES_COLLECTION, getActorId);

  return {
    async create(input: AbsenceCreateInput): Promise<Absence> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = AbsenceCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.startAt !== undefined) optional['startAt'] = fields.startAt;
      if (fields.endAt !== undefined) optional['endAt'] = fields.endAt;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.approvedByWorkerId !== undefined) optional['approvedByWorkerId'] = fields.approvedByWorkerId;
      if (fields.shiftId !== undefined) optional['shiftId'] = fields.shiftId;
      if (fields.availabilityId !== undefined) optional['availabilityId'] = fields.availabilityId;
      if (fields.bookingId !== undefined) optional['bookingId'] = fields.bookingId;
      if (fields.creditId !== undefined) optional['creditId'] = fields.creditId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        workerId: fields.workerId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Absence);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (startAt/endAt sont déjà des
      // Date issues de l'entrée validée).
      return AbsenceSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Absence[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Absence | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: AbsenceUpdateInput): Promise<Absence> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = AbsenceUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, ABSENCES_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`absence ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
